import { T } from '@/i18n/LocaleProvider';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import PageSection from '@/components/PageSection';
import Button from '@/components/Button';
import { MONO_FONT } from '@/theme';

export default function CTASection({
  eyebrow,
  title,
  primaryLabel,
  primaryHref = '/contacto',
  primaryExternal = false,
  primaryTarget,
  primaryRel,
  primaryGtmEvent,
  primaryGtmSource,
  secondaryLabel,
  secondaryHref,
  secondaryGtmEvent,
  secondaryGtmSource,
  secondaryDisabled = false,
  compact = true,
  dark = false,
}) {
  return (
    <PageSection
      compact={compact}
      dark={dark}
      sx={{
        ...(dark && {
          position: 'relative',
          overflow: 'hidden',
          '&::after': {
            content: '""',
            position: 'absolute',
            width: '800px',
            height: '800px',
            left: '-200px',
            bottom: '-300px',
            background:
              'radial-gradient(closest-side, rgba(0,159,139,0.18), transparent 70%)',
            pointerEvents: 'none',
            zIndex: 0,
          },
        }),
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: { md: 'flex-end' },
          justifyContent: 'space-between',
          paddingBottom: '16px',
          gap: { xs: 4, md: 6 },
        }}
      >
        <Box sx={{ flex: 1 }}>
          {eyebrow && (
            <Box
              component="span"
              sx={{
                fontFamily: MONO_FONT,
                fontSize: '11px',
                fontWeight: 500,
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
                color: 'text.accent',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '12px',
                mb: 2,
                '&::before': {
                  content: '""',
                  width: '24px',
                  height: '1px',
                  background: 'currentColor',
                  flexShrink: 0,
                },
              }}
            >
              <T>{eyebrow}</T>
            </Box>
          )}
          <Typography
            variant="h2"
            component="h2"
            sx={{
              fontSize: 'clamp(28px, 3.5vw, 48px)',
              color: dark ? 'common.white' : 'text.primary',
              maxWidth: '24ch',
            }}
          >
            <T>{title}</T>
          </Typography>
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 1.5,
            flexShrink: 0,
          }}
        >
          <Button
            href={primaryHref}
            variant="contained"
            arrow
            {...(primaryExternal && { external: true })}
            {...(primaryTarget && { target: primaryTarget })}
            {...(primaryRel && { rel: primaryRel })}
            {...(primaryGtmEvent && { 'data-gtm-event': primaryGtmEvent })}
            {...(primaryGtmSource && { 'data-gtm-source': primaryGtmSource })}
          >
            <T>{primaryLabel}</T>
          </Button>
          {secondaryLabel && (
            <Button
              href={secondaryDisabled ? undefined : secondaryHref}
              variant="ghost"
              {...(secondaryGtmEvent && {
                'data-gtm-event': secondaryGtmEvent,
              })}
              {...(secondaryGtmSource && {
                'data-gtm-source': secondaryGtmSource,
              })}
              sx={{
                ...(dark
                  ? {
                      color: 'grey.300',
                      borderColor: 'grey.700',
                      '&:hover': {
                        borderColor: 'grey.500',
                        color: 'common.white',
                        backgroundColor: 'rgba(255,255,255,0.06)',
                      },
                    }
                  : {}),
                ...(secondaryDisabled && {
                  opacity: 0.35,
                  pointerEvents: 'none',
                  cursor: 'default',
                }),
              }}
            >
              <T>{secondaryLabel}</T>
            </Button>
          )}
        </Box>
      </Box>
    </PageSection>
  );
}
